import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Empty, Space, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import Modal from '../../../../components/Modal';
import { ItemHistoryEntry } from '../../../../lib/api/settlementDateAdjustment';
import { settlementDateAdjustmentQuery } from '../../queries';
import { SETTLEMENT_DATE_ADJUSTMENT_SOURCE_LABEL } from '../../constants/labels';
import { formatDate } from '../../../../lib/utils/date/formatDate';
import { DATE_FORMAT } from '../../../../types/common/date';

type Props = {
  settlementItemId: number | null;
  onClose: () => void;
};

/**
 * 정산번호 클릭 시 뜨는 "이 정산건의 변경 이력" 모달.
 *
 * settlementDateAdjustmentItem 컬렉션을 settlementItemId 기준으로 뽑고
 * 각 배치 헤더와 조인해 시간순 리스트로 보여준다. 이동/롤백 상태까지 한 화면.
 */
export const ItemHistoryModal = ({ settlementItemId, onClose }: Props): React.JSX.Element => {
  const query = useQuery({
    ...settlementDateAdjustmentQuery.itemHistory(settlementItemId ?? 0),
    enabled: settlementItemId != null,
  });

  const entries = query.data?.entries ?? [];

  const columns: ColumnsType<ItemHistoryEntry> = [
    {
      title: '실행 일시',
      dataIndex: 'executedAt',
      width: 170,
      render: (v?: string) => formatDate(v, DATE_FORMAT.FULL_DASH_FORMAT),
    },
    {
      title: '실행 관리자',
      dataIndex: 'adminName',
      width: 110,
    },
    {
      title: '선정 방식',
      dataIndex: 'source',
      width: 120,
      render: (v?: ItemHistoryEntry['source']) =>
        v ? (
          <Tag color={v === 'excelUpload' ? 'geekblue' : 'default'}>{SETTLEMENT_DATE_ADJUSTMENT_SOURCE_LABEL[v]}</Tag>
        ) : (
          '-'
        ),
    },
    {
      title: '이동 내용',
      key: 'shift',
      width: 130,
      render: (_: unknown, r: ItemHistoryEntry) =>
        r.shiftMode === 'delta' ? `+${r.deltaDays ?? 0}일` : `→ ${r.targetBaseDate ?? '-'}`,
    },
    {
      title: '변경 전 기준일',
      dataIndex: 'beforeBaseDate',
      width: 120,
    },
    {
      title: '변경 후 기준일',
      dataIndex: 'afterBaseDate',
      width: 120,
    },
    {
      title: '변경 전 예정일',
      dataIndex: 'beforeDueDate',
      width: 120,
    },
    {
      title: '변경 후 예정일',
      dataIndex: 'afterDueDate',
      width: 120,
    },
    {
      title: '롤백 여부',
      key: 'rollback',
      width: 140,
      render: (_: unknown, r: ItemHistoryEntry) =>
        r.rolledBackAt ? (
          <Tag color="default">롤백됨 ({formatDate(r.rolledBackAt, DATE_FORMAT.YMD_DASH_FORMAT)})</Tag>
        ) : (
          <Tag color="blue">변경 적용 중</Tag>
        ),
    },
    {
      title: '변경 사유',
      dataIndex: 'reason',
      ellipsis: true,
    },
    {
      title: '배치 ID',
      dataIndex: 'batchId',
      width: 220,
      ellipsis: true,
    },
  ];

  return (
    <Modal
      title={settlementItemId != null ? `변경 이력 · 정산번호 ${settlementItemId}` : '변경 이력'}
      isOpen={settlementItemId != null}
      width={1200}
      onClose={onClose}
      padding="20px"
      destroyOnClose
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message="이 정산건이 포함됐던 모든 기준일 변경 배치의 이동/롤백 기록입니다." />
        <Table<ItemHistoryEntry>
          size="small"
          rowKey={(r) => `${r.batchId}_${r.createdAt}`}
          loading={query.isLoading}
          dataSource={entries}
          columns={columns}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="이 정산건에 대한 기준일 변경 이력이 없습니다." />
            ),
          }}
        />
      </Space>
    </Modal>
  );
};
